import type { GetCurrentMemberFn } from '@/features/iam'
import { type Job } from '@/features/jobs/Job'
import { type JobStore } from '@/features/jobs/JobStore'
import { type QuestionListViewModel } from '@/features/jobs/questions/QuestionListViewModel'
import { task } from 'mobx-task'
import { type QuestionStore } from '@/features/jobs/questions/QuestionStore'
import { combineLatest } from 'rxjs'
import { toStream } from '@/utils/mobx-util'
import { distinctUntilChanged, filter, switchMap } from 'rxjs/operators'
import { unpair, withAbortSignal } from '@/utils/rx-util'
import { ABORTED, resolveOnAbort } from '@/utils/abort-util'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { Revalidation } from '@/infra/revalidation'
import { Toggle } from '@/infra/Toggle'
import { action, computed, observable, runInAction } from 'mobx'
import { orderBy } from 'lodash-es'
import { TabController } from '@/infra/TabController'
import { AmendmentsTableViewModel } from '@/features/jobs/details/components/AmendmentsCard/AmendmentsTable/AmendmentsTableViewModel'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { type QuestionEditorViewModel } from '@/features/jobs/questions/QuestionEditorViewModel'

/**
 * A group of intake answers under one of the questionnaire's section breaks.
 */
export interface IntakeSection {
  title: string | null
  questions: QuestionEditorViewModel[]
}

/**
 * Captured Information section tab.
 */
export type CapturedInformationTab =
  'Questions' | 'Notes' | 'BusinessInfo' | 'PersonalInfo' | 'RefundInfo'

/**
 * Which job events to show.
 */
export type EventFilter = 'All' | 'JobUpdates' | 'TaskUpdates'

export type ResponsesInformationTab =
  'Questions' | 'Notes' | 'PersonalInfo' | 'BusinessInfo' | 'RefundInfo'

/**
 * The sections of a job's details: amendments, events, responses and captured
 * information.
 */
export class DetailsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _responsesRevalidation = new Revalidation(
    () => this.fetchResponses.pending,
  )

  /**
   * The amendments list.
   */
  readonly amendmentsTableVm

  /**
   * The responses (questions).
   */
  readonly responses: QuestionListViewModel

  /**
   * Whether the Responses section is showing.
   */
  readonly responsesSection = new Toggle()

  /**
   * Whether the complete onboarding is showed.
   */
  readonly completeOnboarding = new Toggle()

  /**
   * Whether the questions have been completed.
   */
  @observable
  accessor questionsCompleted = false

  /**
   * Event filter.
   */
  @observable accessor eventFilter: EventFilter = 'All'

  /**
   * TabController for the Captured Information section tabs.
   */
  readonly capturedInformationTabController =
    new TabController<CapturedInformationTab>(() => [
      {
        tab: 'Questions',
        title: 'Questions',
        hasError: () => false,
      },
      {
        tab: 'Notes',
        title: 'Notes',
        hasError: () => false,
      },
      {
        tab: 'BusinessInfo',
        title: 'Business',
        hasError: () => false,
      },
      {
        tab: 'PersonalInfo',
        title: 'Personal',
        hasError: () => false,
      },
      {
        tab: 'RefundInfo',
        title: 'Refund',
        hasError: () => false,
      },
    ])

  readonly informationTabController =
    new TabController<ResponsesInformationTab>(() => [
      {
        tab: 'Questions',
        title: 'Questions',
        hasError: () => false,
      },
      {
        tab: 'Notes',
        title: 'Notes',
        hasError: () => false,
      },
      {
        tab: 'PersonalInfo',
        title: 'Personal',
        hasError: () => false,
      },
      {
        tab: 'BusinessInfo',
        title: 'Business',
        hasError: () => false,
      },
      {
        tab: 'RefundInfo',
        title: 'Refund',
        hasError: () => false,
      },
    ])

  readonly jobEventsScrollProvider = new InfiniteScrollProvider({
    fetchItems: ({ paging, signal }) =>
      this._jobStore.listJobEvents(this.job.id, paging, signal),
    limit: 100,
  })

  constructor(
    public readonly job: Job,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _jobStore: JobStore,
    private readonly _questionStore: QuestionStore,
    createQuestionListViewModel: () => QuestionListViewModel,
    private readonly _useEditDraftJobForFirmAdmin: () => boolean,
  ) {
    this.amendmentsTableVm = new AmendmentsTableViewModel(this.job)

    this.responses = createQuestionListViewModel()

    // Refetches the responses whenever the answers change while a section that
    // shows them is enabled.
    this._lifecycle.manage(
      combineLatest([
        toStream(() => this.job.answers, true),
        this.responsesSection.isEnabled$,
        this.completeOnboarding.isEnabled$,
      ]).pipe(
        filter(
          ([, showingResponses, showingCompleteOnboarding]) =>
            showingResponses || showingCompleteOnboarding,
        ),
        unpair(),
        distinctUntilChanged(),
        switchMap(() =>
          withAbortSignal((signal) =>
            // The responses render a failure from the task's state.
            this.fetchResponses(signal).catch(() => undefined),
          ),
        ),
      ),
    )
  }

  /**
   * All job events.
   */
  @computed
  get allEvents() {
    return this._jobStore.eventsForJob(this.job)
  }

  /**
   * Whether the events are refetching while the previous ones stay on screen.
   */
  @computed
  get isRevalidatingEvents() {
    return this.jobEventsScrollProvider.isRevalidating
  }

  /**
   * Whether the responses are refetching while the previous ones stay on
   * screen.
   */
  @computed
  get isRevalidatingResponses() {
    return this._responsesRevalidation.isRevalidating
  }

  /**
   * Job events, newest first.
   */
  @computed
  get events() {
    return orderBy(this.allEvents, ['dateCreated'], ['desc'])
  }

  /**
   * Job events filtered.
   */
  @computed
  get filteredEvents() {
    const eventFilter = this.eventFilter
    return this.events.filter((e) => {
      switch (eventFilter) {
        case 'All':
          return true
        default:
          return (
            (eventFilter === 'TaskUpdates') ===
            (e.eventType === 'TaskMarkedComplete' ||
              e.eventType === 'TaskMarkedIncomplete')
          )
      }
    })
  }

  /**
   * The questionnaire's own section breaks (Breaker questions) become
   * collapsible groups, so a 40-question intake reads as a handful of topics.
   */
  @computed
  get responseSections(): IntakeSection[] {
    const sections: IntakeSection[] = [{ title: null, questions: [] }]
    this.responses.questionVms.forEach((q) => {
      if (q.editor.type === 'Breaker') {
        sections.push({ title: q.title, questions: [] })
      } else {
        sections[sections.length - 1].questions.push(q)
      }
    })
    return sections.filter((s) => s.questions.length > 0)
  }

  /**
   * Whether the current user is allowed to view the Captured information card.
   */
  @computed
  get canViewCapturedInfoCardAsAdmin() {
    return this._getCurrentMember(this.job.workspace.id).permissions
      .adminLegendView
  }

  /**
   * Whether the current member is able to edit the draft in job details
   */
  @computed
  get useEditDraftJobForFirmAdmin() {
    return this._useEditDraftJobForFirmAdmin()
  }

  @computed
  get canViewCapturedInfoCard() {
    return (
      this.canViewCapturedInfoCardAsAdmin || this.useEditDraftJobForFirmAdmin
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    // Both layouts show the responses (or their count) as soon as the job
    // opens.
    this.responsesSection.enable()
    // The events render a failure from the task's state.
    void this.jobEventsScrollProvider
      .fetch({ signal: this._lifecycle.signal, staleWhileRevalidate: true })
      .catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Sets the event filter.
   *
   * @param filter
   */
  @action.bound
  setEventFilter(filter: EventFilter) {
    this.eventFilter = filter
  }

  /**
   * Fetches questions and variables responses and updates them.
   */
  fetchResponses = task(async (signal: AbortSignal) => {
    const result = await resolveOnAbort(
      // Stored job answers carry no question type and can't be sent back, so
      // the questions follow from the answers the job already has.
      this._questionStore.fetchQuestions({
        jobId: this.job.id,
        answers: null,
        signal,
      }),
      signal,
    )
    if (result !== ABORTED) {
      runInAction(() => {
        this.responses.reconcile(result.questions)
        this.questionsCompleted = result.intakeCompleted
        this._responsesRevalidation.markLoaded()
      })
    }
  })
}
